import { useState, Fragment } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Info, ChevronDown, ChevronRight, Search, X, Filter } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const PlanningRawMaterialsPage = () => {
  const [expandedRows, setExpandedRows] = useState({});
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedStockStatus, setSelectedStockStatus] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [showFilterModal, setShowFilterModal] = useState(false);

  const toggleRow = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const openDetailModal = (material) => {
    setSelectedMaterial(material);
    setShowDetailModal(true);
  };
  // Calculate weeks on hand based on inventory and average weekly usage
  const calculateWeeksOnHand = (inventory, avgWeeklyUsage) => {
    if (avgWeeklyUsage === 0) return 999;
    return Math.max(0, inventory / avgWeeklyUsage);
  };

  // Generate week headers as "Week Of" dates (Monday of each week)
  const getWeekHeaders = () => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    
    // Calculate the Monday of the current week
    // If today is Sunday (0), we go back 6 days to get to Monday
    // If today is Monday (1), we stay at today
    // If today is any other day, we go back (dayOfWeek - 1) days
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const currentMonday = new Date(today);
    currentMonday.setDate(today.getDate() - daysToSubtract);
    
    // Generate 13 week headers
    const weeks = [];
    for (let i = 0; i < 13; i++) {
      const weekDate = new Date(currentMonday);
      weekDate.setDate(currentMonday.getDate() + (i * 7));
      const month = weekDate.getMonth() + 1;
      const day = weekDate.getDate();
      weeks.push(`${month}/${day}`);
    }
    return weeks;
  };

  const weekHeaders = getWeekHeaders();

  // Format date with day abbreviation (e.g., "Wed 10/22")
  const formatDateWithDay = (dateStr) => {
    const date = new Date(dateStr);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const day = days[date.getDay()];
    const month = date.getMonth() + 1;
    const dayNum = date.getDate();
    return `${day} ${month}/${dayNum}`;
  };

  // Mock data for raw materials MRP
  // Formula: Ending Balance = Beginning + Purchase Orders - Planned Usage
  const rawMaterialsData = [
    {
      id: 1,
      sku: "RM-GB-001",
      name: "Ground Beef",
      category: "Proteins",
      supplier: "Sysco",
      unit: "lbs",
      avgWeeklyUsage: 780,
      beginningInventory: 2500,
      weeks: [
        { po: 1000, poDetails: [{ ref: "PO-10234", qty: 1000, vendor: "Sysco", date: "2025-10-20" }], usage: 800, usageDetails: [{ ref: "WO-00123", qty: 500, item: "Burrito", date: "2025-10-22" }, { ref: "WO-00124", qty: 300, item: "Taco Bowl", date: "2025-10-23" }], ending: 2700 },
        { po: 0, poDetails: [], usage: 750, usageDetails: [{ ref: "WO-00125", qty: 450, item: "Burrito", date: "2025-10-27" }, { ref: "WO-00126", qty: 300, item: "Quesadilla", date: "2025-10-28" }], ending: 1950 },
        { po: 1500, poDetails: [{ ref: "PO-10235", qty: 1500, vendor: "Sysco", date: "2025-11-05" }], usage: 820, usageDetails: [{ ref: "WO-00127", qty: 520, item: "Burrito", date: "2025-11-02" }, { ref: "WO-00128", qty: 300, item: "Taco Bowl", date: "2025-11-03" }], ending: 2630, hasIntraWeekNegative: true },
        { po: 0, poDetails: [], usage: 780, usageDetails: [{ ref: "WO-00129", qty: 480, item: "Burrito" }, { ref: "WO-00130", qty: 300, item: "Quesadilla" }], ending: 1850 },
        { po: 1000, poDetails: [{ ref: "PO-10236", qty: 1000, vendor: "Sysco", date: "2025-11-17" }], usage: 800, usageDetails: [{ ref: "WO-00131", qty: 500, item: "Burrito" }, { ref: "WO-00132", qty: 300, item: "Taco Bowl" }], ending: 2050 },
        { po: 0, poDetails: [], usage: 750, usageDetails: [{ ref: "WO-00133", qty: 450, item: "Burrito" }, { ref: "WO-00134", qty: 300, item: "Quesadilla" }], ending: 1300 },
        { po: 1500, poDetails: [{ ref: "PO-10237", qty: 1500, vendor: "Sysco", date: "2025-12-01" }], usage: 820, usageDetails: [{ ref: "WO-00135", qty: 520, item: "Burrito" }, { ref: "WO-00136", qty: 300, item: "Taco Bowl" }], ending: 1980 },
        { po: 0, poDetails: [], usage: 780, usageDetails: [{ ref: "WO-00137", qty: 480, item: "Burrito" }, { ref: "WO-00138", qty: 300, item: "Quesadilla" }], ending: 1200 },
        { po: 1000, poDetails: [{ ref: "PO-10238", qty: 1000, vendor: "Sysco", date: "2025-12-15" }], usage: 800, usageDetails: [{ ref: "WO-00139", qty: 500, item: "Burrito" }, { ref: "WO-00140", qty: 300, item: "Taco Bowl" }], ending: 1400 },
        { po: 0, poDetails: [], usage: 750, usageDetails: [{ ref: "WO-00141", qty: 450, item: "Burrito" }, { ref: "WO-00142", qty: 300, item: "Quesadilla" }], ending: 650 },
        { po: 2000, poDetails: [{ ref: "PO-10239", qty: 2000, vendor: "Sysco", date: "2025-12-29" }], usage: 820, usageDetails: [{ ref: "WO-00143", qty: 520, item: "Burrito" }, { ref: "WO-00144", qty: 300, item: "Taco Bowl" }], ending: 1830 },
        { po: 0, poDetails: [], usage: 780, usageDetails: [{ ref: "WO-00145", qty: 480, item: "Burrito" }, { ref: "WO-00146", qty: 300, item: "Quesadilla" }], ending: 1050 },
        { po: 1000, poDetails: [{ ref: "PO-10240", qty: 1000, vendor: "Sysco", date: "2026-01-12" }], usage: 800, usageDetails: [{ ref: "WO-00147", qty: 500, item: "Burrito" }, { ref: "WO-00148", qty: 300, item: "Taco Bowl" }], ending: 1250 }
      ]
    },
    {
      id: 2,
      sku: "RM-FT-002",
      name: "Flour Tortillas",
      category: "Bread & Grains",
      supplier: "Mission Foods",
      unit: "units",
      avgWeeklyUsage: 2500,
      beginningInventory: 5000,
      weeks: [
        { po: 3000, poDetails: [{ ref: "PO-10241", qty: 3000, vendor: "Mission Foods", date: "2025-10-20" }], usage: 2500, usageDetails: [{ ref: "WO-00150", qty: 1500, item: "Burrito" }, { ref: "WO-00151", qty: 1000, item: "Wrap" }], ending: 5500 },
        { po: 0, poDetails: [], usage: 2400, usageDetails: [{ ref: "WO-00152", qty: 1400, item: "Burrito" }, { ref: "WO-00153", qty: 1000, item: "Quesadilla" }], ending: 3100 },
        { po: 3000, poDetails: [{ ref: "PO-10242", qty: 3000, vendor: "Mission Foods", date: "2025-11-03" }], usage: 2600, usageDetails: [{ ref: "WO-00154", qty: 1600, item: "Burrito" }, { ref: "WO-00155", qty: 1000, item: "Wrap" }], ending: 3500 },
        { po: 0, poDetails: [], usage: 2450, usageDetails: [{ ref: "WO-00156", qty: 1450, item: "Burrito" }, { ref: "WO-00157", qty: 1000, item: "Quesadilla" }], ending: 1050 },
        { po: 5000, poDetails: [{ ref: "PO-10243", qty: 5000, vendor: "Mission Foods", date: "2025-11-17" }], usage: 2500, usageDetails: [{ ref: "WO-00158", qty: 1500, item: "Burrito" }, { ref: "WO-00159", qty: 1000, item: "Wrap" }], ending: 3550 },
        { po: 0, poDetails: [], usage: 2400, usageDetails: [{ ref: "WO-00160", qty: 1400, item: "Burrito" }, { ref: "WO-00161", qty: 1000, item: "Quesadilla" }], ending: 1150 },
        { po: 5000, poDetails: [{ ref: "PO-10244", qty: 5000, vendor: "Mission Foods", date: "2025-12-01" }], usage: 2600, usageDetails: [{ ref: "WO-00162", qty: 1600, item: "Burrito" }, { ref: "WO-00163", qty: 1000, item: "Wrap" }], ending: 3550 },
        { po: 0, poDetails: [], usage: 2450, usageDetails: [{ ref: "WO-00164", qty: 1450, item: "Burrito" }, { ref: "WO-00165", qty: 1000, item: "Quesadilla" }], ending: 1100 },
        { po: 5000, poDetails: [{ ref: "PO-10245", qty: 5000, vendor: "Mission Foods", date: "2025-12-15" }], usage: 2500, usageDetails: [{ ref: "WO-00166", qty: 1500, item: "Burrito" }, { ref: "WO-00167", qty: 1000, item: "Wrap" }], ending: 3600 },
        { po: 0, poDetails: [], usage: 2400, usageDetails: [{ ref: "WO-00168", qty: 1400, item: "Burrito" }, { ref: "WO-00169", qty: 1000, item: "Quesadilla" }], ending: 1200 },
        { po: 5000, poDetails: [{ ref: "PO-10246", qty: 5000, vendor: "Mission Foods", date: "2025-12-29" }], usage: 2600, usageDetails: [{ ref: "WO-00170", qty: 1600, item: "Burrito" }, { ref: "WO-00171", qty: 1000, item: "Wrap" }], ending: 3600 },
        { po: 0, poDetails: [], usage: 2450, usageDetails: [{ ref: "WO-00172", qty: 1450, item: "Burrito" }, { ref: "WO-00173", qty: 1000, item: "Quesadilla" }], ending: 1150 },
        { po: 5000, poDetails: [{ ref: "PO-10247", qty: 5000, vendor: "Mission Foods", date: "2026-01-12" }], usage: 2500, usageDetails: [{ ref: "WO-00174", qty: 1500, item: "Burrito" }, { ref: "WO-00175", qty: 1000, item: "Wrap" }], ending: 3650 }
      ]
    },
    {
      id: 3,
      sku: "RM-CC-003",
      name: "Cheddar Cheese",
      category: "Dairy",
      supplier: "US Foods",
      unit: "lbs",
      avgWeeklyUsage: 400,
      beginningInventory: 1200,
      weeks: Array.from({ length: 13 }, (_, i) => ({
        po: i % 2 === 0 ? (i < 4 ? 500 : 800) : 0,
        poDetails: i % 2 === 0 ? [{ ref: `PO-103${50 + i}`, qty: i < 4 ? 500 : 800, vendor: "US Foods", date: `2025-${10 + Math.floor(i/2)}-${15 + i}` }] : [],
        usage: [400, 380, 420, 390, 400, 380, 420, 390, 400, 380, 420, 390, 400][i],
        usageDetails: [{ ref: `WO-002${10 + i}`, qty: [400, 380, 420, 390, 400, 380, 420, 390, 400, 380, 420, 390, 400][i], item: "Burrito" }],
        ending: [1300, 920, 1000, 610, 1010, 630, 1010, 620, 1020, 640, 1020, 630, 1030][i]
      }))
    },
    {
      id: 4,
      sku: "RM-GC-004",
      name: "Green Chili",
      category: "Produce",
      supplier: "Hatch Chile Co",
      unit: "lbs",
      avgWeeklyUsage: 250,
      beginningInventory: 800,
      weeks: Array.from({ length: 13 }, (_, i) => ({
        po: i % 2 === 0 ? (i < 4 ? [300, 0, 400, 0][i] : 500) : 0,
        poDetails: i % 2 === 0 && (i < 4 ? [300, 0, 400, 0][i] : 500) > 0 ? [{ ref: `PO-104${50 + i}`, qty: i < 4 ? [300, 0, 400, 0][i] : 500, vendor: "Hatch Chile", date: `2025-${10 + Math.floor(i/2)}-${15 + i}` }] : [],
        usage: [250, 240, 260, 245, 250, 240, 260, 245, 250, 240, 260, 245, 250][i],
        usageDetails: [{ ref: `WO-003${10 + i}`, qty: [250, 240, 260, 245, 250, 240, 260, 245, 250, 240, 260, 245, 250][i], item: "Burrito" }],
        ending: [850, 610, 750, 505, 655, 415, 655, 410, 660, 420, 660, 415, 665][i]
      }))
    },
    {
      id: 5,
      sku: "RM-BA-005",
      name: "Bacon",
      category: "Proteins",
      supplier: "Hormel",
      unit: "lbs",
      avgWeeklyUsage: 320,
      beginningInventory: 950,
      weeks: Array.from({ length: 13 }, (_, i) => ({
        po: i % 2 === 0 ? (i < 4 ? 400 : i < 8 ? [500, 0, 600, 0][i-4] : [600, 0, 700, 0, 600][i-8]) : 0,
        poDetails: i % 2 === 0 ? [{ ref: `PO-105${50 + i}`, qty: i < 4 ? 400 : i < 8 ? [500, 0, 600, 0][i-4] : [600, 0, 700, 0, 600][i-8], vendor: "Hormel", date: `2025-${10 + Math.floor(i/2)}-${15 + i}` }] : [],
        usage: [320, 310, 330, 315, 320, 310, 330, 315, 320, 310, 330, 315, 320][i],
        usageDetails: [{ ref: `WO-004${10 + i}`, qty: [320, 310, 330, 315, 320, 310, 330, 315, 320, 310, 330, 315, 320][i], item: "Breakfast Burrito" }],
        ending: [1030, 720, 890, 575, 755, 445, 715, 400, 680, 370, 740, 425, 705][i]
      }))
    },
    {
      id: 6,
      sku: "RM-SA-006",
      name: "Sausage",
      category: "Proteins",
      supplier: "Jimmy Dean",
      unit: "lbs",
      avgWeeklyUsage: 450,
      beginningInventory: 1100,
      weeks: Array.from({ length: 13 }, (_, i) => ({
        po: i % 2 === 0 ? (i < 4 ? 600 : i < 8 ? [700, 0, 800, 0][i-4] : [1000, 0, 900, 0, 800][i-8]) : 0,
        poDetails: i % 2 === 0 ? [{ ref: `PO-106${50 + i}`, qty: i < 4 ? 600 : i < 8 ? [700, 0, 800, 0][i-4] : [1000, 0, 900, 0, 800][i-8], vendor: "Jimmy Dean", date: `2025-${10 + Math.floor(i/2)}-${15 + i}` }] : [],
        usage: [450, 440, 460, 445, 450, 440, 460, 445, 450, 440, 460, 445, 450][i],
        usageDetails: [{ ref: `WO-005${10 + i}`, qty: [450, 440, 460, 445, 450, 440, 460, 445, 450, 440, 460, 445, 450][i], item: "Breakfast Burrito" }],
        ending: [1250, 810, 1050, 605, 855, 415, 755, 310, 860, 420, 860, 415, 765][i]
      }))
    }
  ];

  // Get unique categories and suppliers
  const categories = [...new Set(rawMaterialsData.map(m => m.category))];
  const suppliers = [...new Set(rawMaterialsData.map(m => m.supplier))];
  
  // Stock status options
  const stockStatuses = [
    { id: 'critical', label: 'Critical (< 1 week)', color: 'text-red-700' },
    { id: 'low', label: 'Low (1-3 weeks)', color: 'text-yellow-700' },
    { id: 'good', label: 'Good (3-5 weeks)', color: 'text-green-700' },
    { id: 'overstocked', label: 'Overstocked (> 5 weeks)', color: 'text-blue-700' },
    { id: 'intraweek', label: 'Intra-week Negative', color: 'text-orange-700' }
  ];

  // Helper function to determine stock status
  const getStockStatus = (material) => {
    const hasIntraWeekNegative = material.weeks.some(w => w.hasIntraWeekNegative);
    if (hasIntraWeekNegative) return 'intraweek';
    
    const avgWeeksOnHand = material.weeks.reduce((sum, week) => {
      return sum + calculateWeeksOnHand(week.ending, material.avgWeeklyUsage);
    }, 0) / material.weeks.length;
    
    if (avgWeeksOnHand < 1) return 'critical';
    if (avgWeeksOnHand < 3) return 'low';
    if (avgWeeksOnHand <= 5) return 'good';
    return 'overstocked';
  };

  // Filter materials
  const filteredMaterials = rawMaterialsData.filter(material => {
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSKU = material.sku.toLowerCase().includes(search);
      const matchesName = material.name.toLowerCase().includes(search);
      if (!matchesSKU && !matchesName) return false;
    }
    
    // Category filter
    if (selectedCategories.length > 0) {
      if (!selectedCategories.includes(material.category)) return false;
    }
    
    // Supplier filter
    if (selectedSuppliers.length > 0) {
      if (!selectedSuppliers.includes(material.supplier)) return false;
    }
    
    // Stock status filter
    if (selectedStockStatus.length > 0) {
      const status = getStockStatus(material);
      if (!selectedStockStatus.includes(status)) return false;
    }
    
    return true;
  });

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm("");
    setSelectedCategories([]);
    setSelectedSuppliers([]);
    setSelectedStockStatus([]);
  };

  // Toggle category selection
  const toggleCategory = (category) => {
    setSelectedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  // Toggle supplier selection
  const toggleSupplier = (supplier) => {
    setSelectedSuppliers(prev => 
      prev.includes(supplier) 
        ? prev.filter(s => s !== supplier)
        : [...prev, supplier]
    );
  };

  // Toggle stock status selection
  const toggleStockStatus = (status) => {
    setSelectedStockStatus(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  const getCellColor = (weeksOnHand, hasIntraWeekNegative = false) => {
    if (hasIntraWeekNegative) return "bg-orange-50 text-orange-900 border-2 border-orange-400"; // Warning for intra-week negative
    if (weeksOnHand < 1) return "bg-red-50 text-red-900";
    if (weeksOnHand < 3) return "bg-yellow-50 text-yellow-900";
    if (weeksOnHand <= 5) return "bg-green-50 text-green-900";
    return "bg-blue-50 text-blue-900"; // Overstocked
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Planning - Raw Materials</h1>
        <p className="text-sm text-gray-600 flex items-center gap-2">
          <Info className="w-4 h-4" />
          Projected inventory: Beginning + Purchase Orders (NetSuite) - Planned Usage
        </p>
      </div>

      {/* Compact Filter Bar */}
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            {/* Search Bar */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by SKU or Material..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Filters Button */}
            <button
              onClick={() => setShowFilterModal(true)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              <Filter className="w-4 h-4" />
              Filters
              {(selectedCategories.length > 0 || selectedSuppliers.length > 0 || selectedStockStatus.length > 0) && (
                <span className="ml-1 px-2 py-0.5 text-xs bg-blue-500 text-white rounded-full">
                  {selectedCategories.length + selectedSuppliers.length + selectedStockStatus.length}
                </span>
              )}
            </button>

            {/* Clear Filters Button */}
            {(searchTerm || selectedCategories.length > 0 || selectedSuppliers.length > 0 || selectedStockStatus.length > 0) && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              >
                <X className="w-4 h-4" />
                Clear
              </button>
            )}
          </div>

          {/* Active Filter Badges */}
          {(selectedCategories.length > 0 || selectedSuppliers.length > 0 || selectedStockStatus.length > 0) && (
            <div className="flex flex-wrap gap-2 mt-3">
              {selectedCategories.map(cat => (
                <span key={cat} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                  {cat}
                  <button onClick={() => toggleCategory(cat)} className="hover:text-blue-900">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {selectedSuppliers.map(sup => (
                <span key={sup} className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-800 text-sm rounded-full">
                  {sup}
                  <button onClick={() => toggleSupplier(sup)} className="hover:text-purple-900">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {selectedStockStatus.map(status => {
                const statusObj = stockStatuses.find(s => s.id === status);
                return (
                  <span key={status} className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-800 text-sm rounded-full">
                    {statusObj?.label}
                    <button onClick={() => toggleStockStatus(status)} className="hover:text-gray-900">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}

          {/* Filter Summary */}
          <div className="mt-3 text-sm text-gray-600">
            Showing {filteredMaterials.length} of {rawMaterialsData.length} materials
          </div>
        </CardContent>
      </Card>

      {/* Filter Modal */}
      <Dialog open={showFilterModal} onOpenChange={setShowFilterModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Filter Materials</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Category</label>
              <div className="flex flex-wrap gap-2">
                {categories.map(category => (
                  <button
                    key={category}
                    onClick={() => toggleCategory(category)}
                    className={`px-4 py-2 text-sm rounded-lg border transition-colors ${
                      selectedCategories.includes(category)
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-blue-500'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {/* Supplier Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Supplier</label>
              <div className="flex flex-wrap gap-2">
                {suppliers.map(supplier => (
                  <button
                    key={supplier}
                    onClick={() => toggleSupplier(supplier)}
                    className={`px-4 py-2 text-sm rounded-lg border transition-colors ${
                      selectedSuppliers.includes(supplier)
                        ? 'bg-purple-500 text-white border-purple-500'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-purple-500'
                    }`}
                  >
                    {supplier}
                  </button>
                ))}
              </div>
            </div>

            {/* Stock Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Stock Status</label>
              <div className="space-y-2">
                {stockStatuses.map(status => (
                  <label key={status.id} className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={selectedStockStatus.includes(status.id)}
                      onChange={() => toggleStockStatus(status.id)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className={`text-sm font-medium ${status.color}`}>{status.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              onClick={() => {
                clearFilters();
                setShowFilterModal(false);
              }}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
            >
              Clear All
            </button>
            <button
              onClick={() => setShowFilterModal(false)}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
            >
              Apply Filters
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader>
          <CardTitle>Raw Materials - 13 Week Projection</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-100 sticky top-0 z-10">
                <tr>
                  <th className="sticky z-20 bg-gray-100 text-left py-2 px-1 font-semibold text-gray-700" style={{left: '0px', minWidth: '30px', width: '30px'}}></th>
                  <th className="sticky z-20 bg-gray-100 text-left py-2 px-2 font-semibold text-gray-700 border-r" style={{left: '30px', minWidth: '120px', width: '120px'}}>SKU</th>
                  <th className="sticky z-20 bg-gray-100 text-left py-2 px-2 font-semibold text-gray-700 border-r" style={{left: '150px', minWidth: '200px', width: '200px'}}>Material</th>
                  <th className="sticky z-20 bg-gray-100 text-center py-2 px-2 font-semibold text-gray-700 border-r" style={{left: '350px', minWidth: '50px', width: '50px'}}>Unit</th>
                  <th className="sticky z-20 bg-gray-100 text-center py-2 px-2 font-semibold text-gray-700 border-r bg-blue-50" style={{left: '400px', minWidth: '80px', width: '80px'}}>Begin</th>
                  {weekHeaders.map((weekDate, idx) => (
                    <th key={idx} className="text-center py-3 px-2 font-semibold text-gray-700 border-r" style={{minWidth: '100px'}}>
                      {weekDate}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredMaterials.map((material, idx) => (
                  <>
                    <tr 
                      key={material.id} 
                      className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} cursor-pointer hover:bg-blue-50`}
                      onClick={() => openDetailModal(material)}
                    >
                      <td className="sticky z-10 bg-inherit py-1 px-1" style={{left: '0px'}}>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRow(material.id);
                          }}
                          className="hover:bg-gray-200 rounded p-0.5"
                        >
                          {expandedRows[material.id] ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                        </button>
                      </td>
                      <td className="sticky z-10 bg-inherit py-1 px-2 text-sm text-gray-700 border-r whitespace-nowrap" style={{left: '30px'}}>{material.sku}</td>
                      <td className="sticky z-10 bg-inherit py-1 px-2 text-sm font-medium text-gray-900 border-r" style={{left: '150px'}}>{material.name}</td>
                      <td className="sticky z-10 bg-inherit py-1 px-2 text-center text-xs text-gray-600 border-r" style={{left: '350px'}}>{material.unit}</td>
                      <td className="sticky z-10 bg-inherit py-1 px-2 text-center font-semibold text-gray-900 border-r bg-blue-50" style={{left: '400px'}}>
                        {material.beginningInventory.toLocaleString()}
                      </td>
                      {material.weeks.map((week, weekIdx) => {
                        const weeksOnHand = calculateWeeksOnHand(week.ending, material.avgWeeklyUsage);
                        return (
                          <td key={weekIdx} className="py-1 px-2 border-r text-center">
                            <div className={`font-semibold text-xs px-2 py-1 rounded inline-block ${getCellColor(weeksOnHand, week.hasIntraWeekNegative)}`}>
                              {week.ending.toLocaleString()}
                              <div className="text-[10px] text-gray-600 mt-0.5">{weeksOnHand.toFixed(1)}w</div>
                              {week.hasIntraWeekNegative && <div className="text-[9px] text-orange-700 font-bold mt-0.5">⚠</div>}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                    {expandedRows[material.id] && (
                      <tr className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                        <td className="sticky z-10 bg-inherit" style={{left: '0px'}}></td>
                        <td className="sticky z-10 bg-inherit border-r" style={{left: '30px'}}></td>
                        <td className="sticky z-10 bg-inherit py-1 px-2 text-xs text-gray-600 border-r" style={{left: '150px'}} colSpan="3">
                          <div className="space-y-1">
                            <div className="text-green-700 font-medium">+ Purchase Orders</div>
                            <div className="text-red-700 font-medium">- Planned Usage</div>
                          </div>
                        </td>
                        {material.weeks.map((week, weekIdx) => (
                          <td key={weekIdx} className="py-2 px-2 border-r">
                            <div className="text-xs space-y-1 relative">
                              <div 
                                className="text-green-700 group relative inline-block w-full"
                              >
                                <span className="cursor-help">+{week.po}</span>
                                {week.po > 0 && week.poDetails.length > 0 && (
                                  <div className="hidden group-hover:block absolute z-50 bg-gray-900 text-white text-xs rounded p-2 shadow-lg left-0 top-6 w-56">
                                    {week.poDetails.map((po, i) => (
                                      <div key={i} className="mb-1">
                                        <strong>{po.ref}</strong><br />
                                        {po.qty} {material.unit} from {po.vendor}<br />
                                        Due: <span className="text-yellow-300">{formatDateWithDay(po.date)}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div 
                                className="text-red-700 group relative inline-block w-full"
                              >
                                <span className="cursor-help">-{week.usage}</span>
                                {week.usageDetails.length > 0 && (
                                  <div className="hidden group-hover:block absolute z-50 bg-gray-900 text-white text-xs rounded p-2 shadow-lg left-0 top-6 w-56">
                                    {week.usageDetails.map((u, i) => (
                                      <div key={i} className="mb-1">
                                        <strong>{u.ref}</strong><br />
                                        {u.qty} {material.unit} for {u.item}<br />
                                        {u.date && <span>Scheduled: <span className="text-yellow-300">{formatDateWithDay(u.date)}</span></span>}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        ))}
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="mt-4 flex items-center gap-4 text-xs text-gray-600">
        <div className="flex items-center gap-2">
          <div className="w-12 h-6 bg-orange-50 border-2 border-orange-400 rounded"></div>
          <span>⚠️ Intra-week Negative</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-12 h-6 bg-blue-50 border border-blue-200 rounded"></div>
          <span>Overstocked (5+ weeks)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-12 h-6 bg-green-50 border border-green-200 rounded"></div>
          <span>Good Stock (3-5 weeks)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-12 h-6 bg-yellow-50 border border-yellow-200 rounded"></div>
          <span>Low Stock (1-3 weeks)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-12 h-6 bg-red-50 border border-red-200 rounded"></div>
          <span>Critical (&lt;1 week)</span>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedMaterial && (
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl">{selectedMaterial.sku} - {selectedMaterial.name}</DialogTitle>
              <p className="text-sm text-gray-500">Detailed 13-Week Projection</p>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Inventory Chart with Weeks on Hand */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Inventory Position & Weeks on Hand</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={selectedMaterial.weeks.map((w, idx) => ({ 
                      week: `W${idx + 1}`, 
                      inventory: w.ending,
                      weeksOnHand: calculateWeeksOnHand(w.ending, selectedMaterial.avgWeeklyUsage)
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="week" />
                      <YAxis yAxisId="left" label={{ value: `Inventory (${selectedMaterial.unit})`, angle: -90, position: 'insideLeft' }} />
                      <YAxis yAxisId="right" orientation="right" label={{ value: 'Weeks on Hand', angle: 90, position: 'insideRight' }} />
                      <Tooltip />
                      <Line yAxisId="left" type="monotone" dataKey="inventory" stroke="#3b82f6" strokeWidth={2} name="Inventory" />
                      <Line yAxisId="right" type="monotone" dataKey="weeksOnHand" stroke="#10b981" strokeWidth={2} name="Weeks on Hand" />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Detailed Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Weekly Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {selectedMaterial.weeks.map((week, idx) => {
                      const weeksOnHand = calculateWeeksOnHand(week.ending, selectedMaterial.avgWeeklyUsage);
                      
                      // Calculate day-by-day breakdown
                      const startOfWeek = new Date(2025, 9, 19 + (idx * 7)); // Starting from Oct 19, 2025
                      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                      const dayByDay = [];
                      
                      let runningBalance = idx === 0 ? selectedMaterial.beginningInventory : selectedMaterial.weeks[idx - 1].ending;
                      
                      // Create transactions with dates
                      const transactions = [
                        ...week.usageDetails.map(u => ({ ...u, type: 'usage', date: new Date(u.date || startOfWeek) })),
                        ...week.poDetails.map(po => ({ ...po, type: 'po', date: new Date(po.date) }))
                      ].sort((a, b) => a.date - b.date);
                      
                      // Generate day-by-day view
                      for (let d = 0; d < 7; d++) {
                        const currentDate = new Date(startOfWeek);
                        currentDate.setDate(startOfWeek.getDate() + d);
                        const dayTransactions = transactions.filter(t => t.date.toDateString() === currentDate.toDateString());
                        
                        dayByDay.push({
                          day: days[d],
                          date: currentDate,
                          transactions: dayTransactions,
                          startBalance: runningBalance
                        });
                        
                        // Update running balance for this day
                        dayTransactions.forEach(t => {
                          if (t.type === 'po') runningBalance += t.qty;
                          else runningBalance -= t.qty;
                        });
                      }
                      
                      return (
                        <div key={idx} className="border rounded-lg p-4 bg-gray-50">
                          <div className="grid grid-cols-3 gap-4 mb-4">
                            <div>
                              <h4 className="font-semibold text-gray-900">Week {idx + 1}</h4>
                              <p className="text-xs text-gray-500">Day-by-Day Breakdown</p>
                            </div>
                            <div className="text-center">
                              <div className={`px-3 py-1 rounded font-semibold text-sm ${getCellColor(weeksOnHand, week.hasIntraWeekNegative)}`}>
                                {week.ending.toLocaleString()} {selectedMaterial.unit}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-semibold text-gray-700">
                                {weeksOnHand.toFixed(1)} Weeks on Hand
                              </div>
                              <div className="text-xs text-gray-500">
                                Avg: {selectedMaterial.avgWeeklyUsage} {selectedMaterial.unit}/wk
                              </div>
                            </div>
                          </div>
                          
                          {/* Day-by-Day Table */}
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs border">
                              <thead className="bg-gray-200">
                                <tr>
                                  <th className="border px-2 py-1 text-left">Day</th>
                                  <th className="border px-2 py-1 text-left">Transaction</th>
                                  <th className="border px-2 py-1 text-right">Amount</th>
                                  <th className="border px-2 py-1 text-right">Balance</th>
                                </tr>
                              </thead>
                              <tbody>
                                {dayByDay.map((day, dayIdx) => {
                                  let dayBalance = day.startBalance;
                                  const rows = [];
                                  
                                  if (day.transactions.length === 0) {
                                    rows.push(
                                      <tr key={`${dayIdx}-empty`} className="bg-white">
                                        <td className="border px-2 py-1 font-medium">{day.day} {day.date.getMonth()+1}/{day.date.getDate()}</td>
                                        <td className="border px-2 py-1 text-gray-400 italic" colSpan="2">No activity</td>
                                        <td className="border px-2 py-1 text-right font-medium">{dayBalance.toLocaleString()}</td>
                                      </tr>
                                    );
                                  } else {
                                    day.transactions.forEach((trans, transIdx) => {
                                      const isPO = trans.type === 'po';
                                      const amount = isPO ? trans.qty : -trans.qty;
                                      dayBalance += amount;
                                      
                                      rows.push(
                                        <tr key={`${dayIdx}-${transIdx}`} className={dayBalance < 0 ? 'bg-red-100' : 'bg-white'}>
                                          {transIdx === 0 && (
                                            <td className="border px-2 py-1 font-medium" rowSpan={day.transactions.length}>
                                              {day.day} {day.date.getMonth()+1}/{day.date.getDate()}
                                            </td>
                                          )}
                                          <td className={`border px-2 py-1 ${isPO ? 'text-green-700' : 'text-red-700'}`}>
                                            <a href="#" className="text-blue-600 hover:underline font-medium">{trans.ref}</a>
                                            <span className="text-gray-600 ml-1">- {isPO ? trans.vendor : trans.item}</span>
                                          </td>
                                          <td className={`border px-2 py-1 text-right font-medium ${isPO ? 'text-green-700' : 'text-red-700'}`}>
                                            {isPO ? '+' : ''}{amount.toLocaleString()}
                                          </td>
                                          <td className={`border px-2 py-1 text-right font-bold ${dayBalance < 0 ? 'text-red-700' : 'text-gray-900'}`}>
                                            {dayBalance.toLocaleString()}
                                          </td>
                                        </tr>
                                      );
                                    });
                                  }
                                  
                                  return <Fragment key={`day-${dayIdx}`}>{rows}</Fragment>;
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default PlanningRawMaterialsPage;
